// The trip to work in the listing popup (Restid till jobbet):
// - TripTile: a small tile in the photo collage (desktop), the route's shape drawn without a map (TripMap);
//   a click shows the trip in the main photo's place.
// - TripHero: the trip in the main photo's place on a real map (CommuteTripMap.js), with buttons for the means
//   of travel, the legs in a card and a Bilder / Resan switch back to the photos.
// - TripPanel: the same on phones, inside the Till jobbet card (the collage there is one photo).
// The line follows the real route in each leg's colour (tripMap.js); walking is dotted. Without the base map
// (OpenFreeMap down, no WebGL) the big views fall back to the drawing.
import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { commuteModeLabel } from './commute.js';
import { CommuteModeIcon } from './CommuteFilter.js';
import CommuteTripMap from './CommuteTripMap.js';
import {
    fitTrip, legColour, legKind, legName, midpoint, pathData, placeBox, scaleBar, stopName, textWidth, tripSegments,
} from './tripMap.js';
import './commute.css';
import './commuteTrip.css';

const HOUSE_PATH = 'M3 11l9-7 9 7v9a1 1 0 0 1-1 1h-5v-6h-6v6H4a1 1 0 0 1-1-1z';
const WORK_PATH = 'M3 9a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2zM9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2';

const LABEL_FONT = '500 12px Roboto, Arial, sans-serif';
const BADGE_FONT = '700 12px Roboto, Arial, sans-serif';

const useBoxSize = (ref) => {
    const [size, setSize] = useState(null);
    useLayoutEffect(() => {
        const element = ref.current;
        if (!element) return undefined;
        const update = () => {
            const next = { w: element.clientWidth, h: element.clientHeight };
            setSize((prev) => (prev && prev.w === next.w && prev.h === next.h ? prev : next));
        };
        update();
        if (typeof ResizeObserver === 'undefined') return undefined;
        const observer = new ResizeObserver(update);
        observer.observe(element);
        return () => observer.disconnect();
    }, [ref]);
    return size;
};

const SIZES = {
    tile: { line: 4, walk: 2.5, casing: 3, end: 7, stop: 0 },
    hero: { line: 6, walk: 3.5, casing: 4, end: 14, stop: 4.5 },
    card: { line: 5, walk: 3, casing: 4, end: 12, stop: 4 },
};

const workName = (label) => String(label || '').split(',')[0].trim();

// The map itself. `pad` keeps the line clear of what floats over it (the hero's card and buttons).
export const TripMap = ({ route, mode, home, work, workLabel, variant = 'hero', pad, swedish, ariaLabel }) => {
    const ref = useRef(null);
    const size = useBoxSize(ref);
    const segments = useMemo(() => tripSegments(route, mode), [route, mode]);
    const sizes = SIZES[variant] || SIZES.hero;
    const labelled = variant !== 'tile';

    const drawing = useMemo(() => {
        if (!size || !size.w || !size.h) return null;
        const padding = pad || { left: 24, top: 24, right: 24, bottom: 24 };
        const fit = fitTrip(segments, [home, work], size.w, size.h, padding);
        if (!fit) return null;
        const lines = segments
            .map((segment) => ({ ...segment, pixels: segment.points.map(fit.project) }))
            .filter((segment) => segment.pixels.length > 1);
        const homePx = home ? fit.project(home) : lines[0]?.pixels[0];
        const workPx = work ? fit.project(work) : lines[lines.length - 1]?.pixels.slice(-1)[0];
        const stops = [];
        const badges = [];
        const labels = [];
        if (mode === 'transit') {
            lines.filter((segment) => segment.kind === 'transit').forEach((segment) => {
                stops.push({ at: segment.pixels[0], colour: segment.colour, name: segment.leg.from });
                stops.push({ at: segment.pixels[segment.pixels.length - 1], colour: segment.colour, name: segment.leg.to });
            });
        }
        const scale = labelled ? scaleBar(fit.metresPerPx, 80) : null;
        if (labelled) {
            // Placed greedily: the ends first, then each line's badge, then the stops' names; clear of the
            // two ends' markers and the scale bar.
            const bounds = { x: padding.left - 12, y: 6, w: size.w - padding.left + 6, h: size.h - 12 };
            const taken = [homePx, workPx].filter(Boolean).map(([x, y]) => ({ x: x - sizes.end - 2, y: y - sizes.end - 2, w: 2 * sizes.end + 4, h: 2 * sizes.end + 4 }));
            taken.push({ x: size.w - scale.px - 24, y: size.h - 34, w: scale.px + 24, h: 34 });
            const workText = workName(workLabel);
            if (workPx && workText) {
                const w = textWidth(workText, '600 12px "Space Grotesk", Roboto, sans-serif') + 16;
                const box = placeBox(workPx, w, 22, taken, bounds, sizes.end + 4);
                if (box) labels.push({ box, text: workText, kind: 'end' });
            }
            lines.filter((segment) => segment.kind === 'transit').forEach((segment) => {
                const text = `${legName(segment.leg, swedish)}${segment.leg.line ? ` ${segment.leg.line}` : ''}`;
                const w = textWidth(text, BADGE_FONT) + 16;
                const box = placeBox(midpoint(segment.pixels), w, 22, taken, bounds, 10);
                if (box) badges.push({ box, text, colour: segment.colour });
            });
            const named = new Set();
            stops.forEach((stop) => {
                const text = stopName(stop.name);
                if (!text || text === 'START' || text === 'END' || named.has(text)) return;
                named.add(text);
                const box = placeBox(stop.at, textWidth(text, LABEL_FONT) + 4, 16, taken, bounds, 8);
                if (box) labels.push({ box, text, kind: 'stop' });
            });
        }
        return { lines, homePx, workPx, stops, badges, labels, scale };
    }, [size, segments, home, work, workLabel, pad, mode, labelled, sizes, swedish]);

    return (
        <div ref={ref} className={`trip-map trip-map--${variant}`} role="img" aria-label={ariaLabel}>
            {drawing && (
                <svg width={size.w} height={size.h} aria-hidden="true">
                    <g fill="none" strokeLinecap="round" strokeLinejoin="round">
                        {drawing.lines.map((segment, index) => (segment.kind === 'walk' ? (
                            // eslint-disable-next-line react/no-array-index-key
                            <path key={`w${index}`} className="trip-walk" d={pathData(segment.pixels)}
                                style={{ strokeWidth: sizes.walk, strokeDasharray: `0.5 ${sizes.walk * 2}` }} />
                        ) : (
                            // eslint-disable-next-line react/no-array-index-key
                            <g key={`l${index}`}>
                                <path className="trip-casing" d={pathData(segment.pixels)} style={{ strokeWidth: sizes.line + sizes.casing }} />
                                <path d={pathData(segment.pixels)} style={{ stroke: segment.colour, strokeWidth: sizes.line }} />
                            </g>
                        )))}
                    </g>
                    {sizes.stop > 0 && drawing.stops.map((stop, index) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <circle key={`s${index}`} className="trip-stop" cx={stop.at[0]} cy={stop.at[1]} r={sizes.stop} style={{ stroke: stop.colour }} />
                    ))}
                    {drawing.badges.map((badge, index) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <g key={`b${index}`} className="trip-badge">
                            <rect x={badge.box.x} y={badge.box.y} width={badge.box.w} height={badge.box.h} rx="11" style={{ fill: badge.colour }} />
                            <text x={badge.box.x + badge.box.w / 2} y={badge.box.y + 15.5} textAnchor="middle">{badge.text}</text>
                        </g>
                    ))}
                    {drawing.labels.map((label, index) => (label.kind === 'end' ? (
                        // eslint-disable-next-line react/no-array-index-key
                        <g key={`e${index}`} className="trip-end-label">
                            <rect x={label.box.x} y={label.box.y} width={label.box.w} height={label.box.h} rx="11" />
                            <text x={label.box.x + label.box.w / 2} y={label.box.y + 15} textAnchor="middle">{label.text}</text>
                        </g>
                    ) : (
                        // eslint-disable-next-line react/no-array-index-key
                        <text key={`t${index}`} className="trip-stop-label" x={label.box.x + 2} y={label.box.y + 12}>{label.text}</text>
                    )))}
                    {drawing.homePx && <TripEnd at={drawing.homePx} r={sizes.end} path={HOUSE_PATH} kind="home" />}
                    {drawing.workPx && <TripEnd at={drawing.workPx} r={sizes.end} path={WORK_PATH} kind="work" />}
                    {drawing.scale && (
                        <g className="trip-scale" transform={`translate(${size.w - drawing.scale.px - 16} ${size.h - 16})`}>
                            <path d={`M0 -4V0H${drawing.scale.px.toFixed(1)}V-4`} />
                            <text x={drawing.scale.px / 2} y="-7" textAnchor="middle">{drawing.scale.label}</text>
                        </g>
                    )}
                </svg>
            )}
        </div>
    );
};

const TripEnd = ({ at, r, path, kind }) => (
    <g className={`trip-end trip-end--${kind}`} transform={`translate(${at[0]} ${at[1]})`}>
        <circle r={r} />
        {r >= 10 && (
            <svg x={-r * 0.58} y={-r * 0.6} width={r * 1.16} height={r * 1.16} viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2.4" strokeLinejoin="round" strokeLinecap="round">
                <path d={path} />
            </svg>
        )}
    </g>
);

const tripAria = (route, mode, swedish, workLabel) => {
    const to = workName(workLabel) || (swedish ? 'jobbet' : 'work');
    const minutes = route?.[mode]?.minutes;
    if (mode !== 'transit') {
        return swedish
            ? `${commuteModeLabel(mode, true)} till ${to}, ${minutes} min`
            : `${commuteModeLabel(mode, false)} to ${to}, ${minutes} min`;
    }
    const legs = (route?.transit?.legs || []).map((leg) => `${legName(leg, swedish)}${leg.line ? ` ${leg.line}` : ''} ${leg.minutes} min`);
    return `${swedish ? 'Resan till' : 'The trip to'} ${to}, ${minutes} min: ${legs.join(', ')}`;
};

const TILE_PAD = { left: 18, top: 18, right: 18, bottom: 44 };

// The small collage tile: the trip on the map, and its time.
export const TripTile = ({ route, mode, home, work, workLabel, swedish, dark, shown, onOpen }) => (
    <button
        type="button"
        className={`lfv-tile lfv-trip-tile${shown ? ' is-shown' : ''}`}
        onClick={onOpen}
        aria-pressed={shown}
        aria-label={swedish ? 'Visa resan till jobbet' : 'Show the trip to work'}
    >
        <CommuteTripMap route={route} mode={mode} home={home} work={work} swedish={swedish} dark={dark} pad={TILE_PAD} variant="tile"
            fallback={<TripMap route={route} mode={mode} home={home} work={work} variant="tile" swedish={swedish} pad={TILE_PAD} />} />
        <span className="lfv-trip-tile-label">
            <CommuteModeIcon mode={mode} size={13} />
            {swedish ? 'Resan till jobbet' : 'Trip to work'} · {route?.[mode]?.minutes} min
        </span>
    </button>
);

// The means of travel as buttons, each with its time.
export const TripModeSwitch = ({ route, modes, mode, onChange, swedish }) => (
    <div className="trip-modes" role="radiogroup" aria-label={swedish ? 'Färdsätt' : 'Means of travel'}>
        {modes.map((key) => (
            <button
                key={key}
                type="button"
                role="radio"
                aria-checked={key === mode}
                className={`trip-mode${key === mode ? ' is-active' : ''}`}
                onClick={() => onChange(key)}
                title={commuteModeLabel(key, swedish)}
            >
                <CommuteModeIcon mode={key} size={15} />
                <span className="trip-mode-name">{commuteModeLabel(key, swedish)}</span>
                <span>{route[key].minutes} min</span>
            </button>
        ))}
    </div>
);

const placeName = (name, swedish, end) => {
    if (name === 'START') return swedish ? 'bostaden' : 'the home';
    if (name === 'END') return swedish ? 'jobbet' : 'work';
    return stopName(name) || (end ? (swedish ? 'jobbet' : 'work') : '');
};

const formatKm = (km, swedish) => `${String(km).replace('.', swedish ? ',' : '.')} km`;

// The trip in words: a title line, then the legs (public transport) or one line (bike, car, walking).
export const TripSummary = ({ route, mode, swedish, workLabel, compact = false }) => {
    const trip = route?.[mode];
    if (!trip) return null;
    const transfers = route?.transit?.transfers ?? 0;
    const title = `${trip.minutes} min ${commuteModeLabel(mode, swedish, true)} · ${mode === 'transit'
        ? (transfers === 0 ? (swedish ? 'inga byten' : 'no changes') : `${transfers} ${swedish ? (transfers === 1 ? 'byte' : 'byten') : (transfers === 1 ? 'change' : 'changes')}`)
        : formatKm(trip.km, swedish)}`;
    const to = workName(workLabel);
    const sub = [
        mode === 'car' ? (swedish ? 'Bil utan köer' : 'Car without traffic') : null,
        swedish ? 'Framme 08:30 en vardag' : 'Arriving 08:30 on a weekday',
    ].filter(Boolean).join(' · ');
    return (
        <div className={`trip-summary${compact ? ' is-compact' : ''}`}>
            <div className="trip-summary-head">
                <b>{title}</b>
                <span>{to ? `${swedish ? 'Till' : 'To'} ${to} · ` : ''}{sub}</span>
            </div>
            {mode === 'transit' && (
                <ol className="trip-legs">
                    {(trip.legs || []).map((leg, index) => {
                        const walk = legKind(leg) === 'walk';
                        const change = walk && index > 0 && index < trip.legs.length - 1;
                        return (
                            // eslint-disable-next-line react/no-array-index-key
                            <li key={index} className={change ? 'is-change' : undefined}>
                                {walk
                                    ? <i className="trip-legs-walk" aria-hidden="true" />
                                    : <b className="trip-legs-line" style={{ background: legColour(leg) }}>{leg.line || legName(leg, swedish)}</b>}
                                <span className="trip-legs-text">
                                    {walk
                                        ? (change ? (swedish ? 'Byte' : 'Change') : `${swedish ? 'Gå till' : 'Walk to'} ${placeName(leg.to, swedish, true)}`)
                                        : `${legName(leg, swedish)} ${swedish ? 'till' : 'to'} ${placeName(leg.to, swedish, true)}`}
                                </span>
                                <span className="trip-legs-min">{leg.minutes} min</span>
                            </li>
                        );
                    })}
                </ol>
            )}
        </div>
    );
};

const HERO_CARD_W = 252;

// The trip in the main photo's place (desktop).
export const TripHero = ({ route, modes, mode, onModeChange, home, work, workLabel, swedish, dark, photoCount, onShowPhotos }) => {
    const pad = useMemo(() => ({ left: HERO_CARD_W + 40, top: 64, right: 56, bottom: 52 }), []);
    const aria = tripAria(route, mode, swedish, workLabel);
    return (
        <div className="lfv-trip-hero">
            <CommuteTripMap route={route} mode={mode} home={home} work={work} workLabel={workLabel} swedish={swedish}
                dark={dark} pad={pad} ariaLabel={aria}
                fallback={<TripMap route={route} mode={mode} home={home} work={work} workLabel={workLabel} variant="hero"
                    pad={pad} swedish={swedish} ariaLabel={aria} />} />
            <TripModeSwitch route={route} modes={modes} mode={mode} onChange={onModeChange} swedish={swedish} />
            <div className="lfv-trip-card" style={{ width: HERO_CARD_W }}>
                <TripSummary route={route} mode={mode} swedish={swedish} workLabel={workLabel} />
            </div>
            <div className="lfv-trip-switch" role="group" aria-label={swedish ? 'Visa' : 'Show'}>
                <button type="button" onClick={onShowPhotos}>
                    {swedish ? 'Bilder' : 'Photos'}{photoCount ? ` ${photoCount}` : ''}
                </button>
                <button type="button" className="is-active" aria-pressed="true">
                    {swedish ? 'Resan till jobbet' : 'Trip to work'}
                </button>
            </div>
        </div>
    );
};

const PANEL_PAD = { left: 28, top: 28, right: 28, bottom: 28 };

// The trip inside the Till jobbet card (phones): pinch to zoom; a finger still scrolls the popup.
export const TripPanel = ({ route, modes, mode, onModeChange, home, work, workLabel, swedish, dark }) => {
    const aria = tripAria(route, mode, swedish, workLabel);
    return (
        <div className="lfv-trip-panel">
            <TripModeSwitch route={route} modes={modes} mode={mode} onChange={onModeChange} swedish={swedish} />
            <div className="lfv-trip-panel-map">
                <CommuteTripMap route={route} mode={mode} home={home} work={work} workLabel={workLabel} swedish={swedish}
                    dark={dark} pad={PANEL_PAD} touch ariaLabel={aria}
                    fallback={<TripMap route={route} mode={mode} home={home} work={work} workLabel={workLabel} variant="card"
                        swedish={swedish} ariaLabel={aria} />} />
            </div>
            <TripSummary route={route} mode={mode} swedish={swedish} workLabel={workLabel} compact />
        </div>
    );
};
